import React from 'react'
import { trackAppStoreClick } from '../analytics'

const img = (name: string) => `${process.env.PUBLIC_URL}/images/${name}`
const APP_STORE_URL = 'https://apps.apple.com/us/app/heard-epub-text-to-speech/id6785654562'

interface Feature {
  eyebrow: string
  title: string
  body: string
  image: string
}

const FEATURES: Feature[] = [
  {
    eyebrow: 'Your books, your way',
    title: 'Import your own EPUB library',
    body: 'Heard Pro lets you bring in DRM-free EPUB files and turn the books you already own into private, offline listening. Your books and narration stay on your iPhone.',
    image: 'heard-library-light.jpg',
  },
  {
    eyebrow: 'On-device narration',
    title: 'A neural voice, generated right on your phone',
    body: 'Heard synthesizes natural speech locally — no streaming, no servers, no account. The first time a page is read it’s generated in seconds, then it just plays. Works on a plane, in a tunnel, anywhere.',
    image: 'heard-alice-generating.jpg',
  },
  {
    eyebrow: 'Press play, keep listening',
    title: 'Pages turn themselves as you listen',
    body: 'Narration flows from page to page automatically — set it going and follow along, or simply listen as one page leads into the next.',
    image: 'heard-alice-playing.jpg',
  },
  {
    eyebrow: 'A curated library',
    title: '56 classics, ready to read and hear',
    body: 'Hand-picked works by Austen, Dostoevsky, Dickens, Homer and more are included free. Each public-domain classic is ready to read or hear aloud.',
    image: 'heard-library-dark.jpg',
  },
  {
    eyebrow: 'A reader that disappears',
    title: 'Beautiful, distraction-free pages',
    body: 'Clean typography with adjustable font size and serif, sans, or mono styles. Tap the edges to turn pages, tap the center for full screen. Your place is always saved.',
    image: 'heard-alice.jpg',
  },
  {
    eyebrow: 'Pocket-safe listening',
    title: 'Lock the screen, keep listening',
    body: 'Heard has limited ability to generate audio in the background due to iOS restrictions. For best results, you can tap Lock Screen while a book is playing and the display dims and ignores taps — so you can slip your phone in your pocket without pausing the story or turning a page by accident. Swipe down to unlock.',
    image: 'heard-lockscreen.png',
  },
  {
    eyebrow: 'Faithful to the book',
    title: 'Original illustrations, preserved',
    body: 'Where a classic edition includes artwork, Heard keeps it — so illustrated favorites look the way they were meant to.',
    image: 'heard-illustration.jpg',
  },
  {
    eyebrow: 'Find your next read',
    title: 'Search by title or author',
    body: 'Jump straight to the book you want. The library filters as you type, so the whole shelf is a tap away.',
    image: 'heard-search-odyssey.jpg',
  },
]

const PRO = [
  'Every neural voice — choose the narrator you like best',
  'Import your own EPUBs and listen to any book you own',
  'Support independent, privacy-first development',
]

const INCLUDED_BOOKS = [
  'A Christmas Carol',
  'A Connecticut Yankee in King Arthur’s Court',
  'A Doll’s House',
  'A Room with a View',
  'A Tale of Two Cities',
  'Adventures of Huckleberry Finn',
  'Alice’s Adventures in Wonderland',
  'Anna Karenina',
  'Anne of Green Gables',
  'Around the World in Eighty Days',
  'Beowulf',
  'Crime and Punishment',
  'Don Quixote',
  'Dracula',
  'Emma',
  'Frankenstein',
  'Great Expectations',
  'Gulliver’s Travels',
  'Heart of Darkness',
  'Jane Eyre',
  'Les Misérables',
  'Little Women',
  'Middlemarch',
  'Moby Dick',
  'Oliver Twist',
  'Peter Pan',
  'Pride and Prejudice',
  'Robinson Crusoe',
  'Romeo and Juliet',
  'Sense and Sensibility',
  'The Adventures of Sherlock Holmes',
  'The Adventures of Tom Sawyer',
  'The Blue Castle',
  'The Call of the Wild',
  'The Complete Works of William Shakespeare',
  'The Count of Monte Cristo',
  'The Enchanted April',
  'The Great Gatsby',
  'The History of Tom Jones, a Foundling',
  'The Hound of the Baskervilles',
  'The Iliad',
  'The Importance of Being Earnest',
  'The King in Yellow',
  'The Metamorphosis',
  'The Odyssey',
  'The Picture of Dorian Gray',
  'The Scarlet Letter',
  'The Secret Garden',
  'The Strange Case of Dr. Jekyll and Mr. Hyde',
  'The Three Musketeers',
  'The Time Machine',
  'The War of the Worlds',
  'The Wonderful Wizard of Oz',
  'Treasure Island',
  'Twenty Years After',
  'Wuthering Heights',
]

export default function Home() {
  return (
    <>
      <section className="hero">
        <div className="container hero-inner">
          <div className="hero-copy">
            <div className="hero-brand">
              <img className="hero-icon" src={img('heard-icon.png')} alt="" />
              <span>Heard for iPhone</span>
            </div>
            <h1>Listen to any EPUB on your iPhone</h1>
            <p className="tag">
              A private EPUB reader with natural text to speech, generated on-device so it works offline.
            </p>
            <a
              id="app-store-home-hero"
              className="btn"
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackAppStoreClick('app-store-home-hero')}
            >
              Download on the App Store
            </a>
            <p className="hero-note">Free to download · No ads · No account</p>
            <ul className="hero-points" aria-label="Heard highlights">
              <li>Import your own DRM-free EPUB books with Heard Pro</li>
              <li>Listen without sending your books to a server</li>
              <li>Start with 56 included classics</li>
            </ul>
            <a className="hero-link" href="#features">Explore all features ↓</a>
          </div>

          <div className="hero-demo">
            <div className="demo-heading">
              <span className="eyebrow">Hear it in action</span>
              <span>Watch Heard read an EPUB aloud</span>
            </div>
            <div className="video-frame">
              <iframe
                src="https://www.youtube-nocookie.com/embed/a2SeRVH3xdw?playsinline=1&rel=0"
                title="Heard reading an EPUB aloud on iPhone"
                loading="eager"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <div className="container">
          {FEATURES.map((f, i) => (
            <div key={f.title} className={`row${i % 2 === 1 ? ' reverse' : ''}`}>
              <div className="row-text">
                <div className="eyebrow">{f.eyebrow}</div>
                <h2>{f.title}</h2>
                <p>{f.body}</p>
              </div>
              <div className="row-media">
                <img className="phone" src={img(f.image)} alt={f.title} loading="lazy" />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="library">
        <div className="container">
          <div className="library-heading">
            <div className="eyebrow">Included free</div>
            <h2>56 classics, ready to read and hear</h2>
            <p>
              Every title in the built-in library is included free, with the default voice
              available for read-aloud listening.
            </p>
          </div>
          <ul className="book-grid">
            {INCLUDED_BOOKS.map((book) => (
              <li key={book}>{book}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="pro">
        <div className="container">
          <h2>Heard Pro</h2>
          <p className="sub">Heard is free to read the whole library with one built-in voice. Go Pro to unlock more:</p>
          <ul className="pro-list">
            {PRO.map((p) => (
              <li key={p}>
                <span className="check">✓</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <a
            id="app-store-home-pro"
            className="btn"
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackAppStoreClick('app-store-home-pro')}
          >
            Get Heard for iOS
          </a>
          <p className="free-note">Free to read &amp; listen with the default voice.</p>
        </div>
      </section>
    </>
  )
}
